import { useGetContainerDetails } from '@/hooks/api/dashboard'
import { useBrowserQuery } from '@/hooks/common'
import useAppStore from '@/store/useAppStore'
import colors from '@/styles/colors'
import SVG from 'react-inlinesvg'
import { CheckCircle } from '@mui/icons-material'
import { Box, CircularProgress, Grid, useTheme } from '@mui/material'
import { useEffect } from 'react'
import { EgosContainer, UploadPhotoItem } from './__styled'

import { Container, EgoObject } from '@/types'

import NoDataPlaceholder from '@/components/NoDataPlaceholder'
import { NoDataPlaceholders } from '@/utils/placeholders'

interface Props {
  onSelectEgos: (egoObjects: EgoObject[]) => void
  egoObjects: EgoObject[]
  selectedEgoList: EgoObject[]
  onClickShowDrawer: () => void
  container: Container
  isLoading: boolean
}

export default function UploadPhotos({
  onSelectEgos,
  selectedEgoList,
  container,
  egoObjects,
  isLoading,
}: Props) {
  const query = useBrowserQuery()
  const { containerID } = container
  // const theme = useTheme()
  // const getContainerDetails = useGetContainerDetails()

  useEffect(() => {
    if (containerID) {
      onSelectEgos([])
    }
  }, [container])

  const onClickEgo = (egoObject: EgoObject) => {
    const index = selectedEgoList.findIndex((i) => i.egoID === egoObject.egoID)
    if (index < 0) {
      selectedEgoList.push(egoObject)
    } else {
      selectedEgoList.splice(index, 1)
    }
    onSelectEgos([...selectedEgoList])
  }

  return (
    <Box
      sx={{
        backgroundColor: '#f8fafc',
        height: '100%',
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        padding: '24px',
        borderRadius: '16px',
      }}
    >
      {isLoading && (
        <Box display='flex' justifyContent={'center'} pt={4}>
          <CircularProgress
            size={40}
            thickness={4}
            sx={{
              color: '#6366f1',
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              },
            }}
          />
        </Box>
      )}

      {!isLoading && egoObjects.length === 0 && (
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <NoDataPlaceholder {...NoDataPlaceholders.emptyUploadContainer} />
        </Box>
      )}

      <Grid
        container
        spacing={2}
        sx={{
          overflowX: 'hidden',
          overflowY: 'auto',
          position: 'relative',
          margin: 0,
          width: '100%',
        }}
      >
        {egoObjects.map((ego) => {
          const selected = selectedEgoList.find((e) => e.egoID == ego.egoID)
          return (
            <Grid
              item
              key={ego.egoID}
              xs={6}
              sm={4}
              md={3}
              lg={2.4}
              sx={{ padding: '8px !important' }}
            >
              <Box
                onClick={() => onClickEgo(ego)}
                sx={{
                  position: 'relative',
                  aspectRatio: '1',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  // boxShadow: selected
                  //   ? '0 8px 24px rgba(99, 102, 241, 0.25), 0 4px 8px rgba(99, 102, 241, 0.15)'
                  //   : '0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)',
                  border: selected ? '3px solid #6366f1' : '1px solid rgba(226, 232, 240, 0.8)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&::before': selected
                    ? {
                        content: '""',
                        position: 'absolute',
                        inset: '-4px',
                        borderRadius: '18px',
                        padding: '2px',
                        background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                        WebkitMask:
                          'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        WebkitMaskComposite: 'xor',
                        maskComposite: 'exclude',
                        opacity: 0.3,
                      }
                    : {},
                  '&:hover': {
                    transform: 'translateY(-4px) scale(1.02)',
                    // boxShadow: selected
                    //   ? '0 12px 32px rgba(99, 102, 241, 0.35), 0 8px 16px rgba(99, 102, 241, 0.2)'
                    //   : '0 12px 32px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(0, 0, 0, 0.1)',
                    borderColor: selected ? '#6366f1' : 'rgba(99, 102, 241, 0.3)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    backgroundImage: `url(${ego.egoAlbumURL})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                />

                {/* Selection Overlay */}
                {selected && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background:
                        'linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(168, 85, 247, 0.12) 100%)',
                    }}
                  />
                )}

                {/* Check Icon with Animation */}
                {selected && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4), 0 2px 4px rgba(0, 0, 0, 0.2)',
                      border: '2px solid #ffffff',
                      transform: 'scale(1)',
                      animation: 'checkBounce 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '@keyframes checkBounce': {
                        '0%': { transform: 'scale(0)' },
                        '50%': { transform: 'scale(1.15)' },
                        '100%': { transform: 'scale(1)' },
                      },
                    }}
                  >
                    <CheckCircle sx={{ color: '#ffffff', fontSize: '20px' }} />
                  </Box>
                )}

                {/* Unselected State Indicator */}
                {!selected && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      border: '2px solid rgba(226, 232, 240, 0.8)',
                      opacity: 0,
                      transition: 'opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        opacity: 1,
                      },
                    }}
                  />
                )}
              </Box>
            </Grid>
          )
        })}
      </Grid>
    </Box>
  )
}
